import React from 'react';
import { Box, Typography, Container } from '@mui/material';

const VideoBlock = ({
  videoUrl = '',
  caption = '',
  aspectRatio = '16:9',
  maxWidth = '800px',
  align = 'center',
  padding = '1rem'
}) => {
  console.log('Rendering VideoBlock component with props:', { videoUrl, caption });
  
  // Function to extract video ID from YouTube or Vimeo URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    
    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    
    if (youtubeMatch && youtubeMatch[1]) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/[^\/]*\/videos\/|album\/\d+\/video\/|)(\d+)(?:$|\/|\?))/;
    const vimeoMatch = url.match(vimeoRegex);
    
    if (vimeoMatch && vimeoMatch[1]) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    // If it's already an embed URL, return it
    if (url.includes('youtube.com/embed/') || url.includes('player.vimeo.com/video/')) {
      return url;
    }
    
    // Default case, just return the URL
    return url;
  };
  
  // Calculate aspect ratio for responsive iframe
  const calculatePaddingTop = () => {
    if (aspectRatio === '16:9') return '56.25%';
    if (aspectRatio === '4:3') return '75%';
    if (aspectRatio === '1:1') return '100%';
    return '56.25%'; // Default to 16:9
  };
  
  const embedUrl = getEmbedUrl(videoUrl);
  
  return (
    <Box
      sx={{
        width: '100%',
        padding,
        textAlign: align
      }}
    >
      <Container maxWidth="lg" sx={{ maxWidth }}>
        {embedUrl && (
          <Box
            sx={{
              position: 'relative',
              paddingTop: calculatePaddingTop(),
              width: '100%',
              height: 0,
              marginLeft: align === 'center' ? 'auto' : align === 'right' ? 'auto' : '0',
              marginRight: align === 'center' ? 'auto' : align === 'left' ? 'auto' : '0',
            }}
          >
            <iframe
              src={embedUrl}
              title={caption || 'Video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        )}
        {caption && (
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1, 
              fontStyle: 'italic',
              textAlign: align
            }}
          >
            {caption}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

// Define the fields for the Puck editor
VideoBlock.fields = {
  videoUrl: {
    type: 'text',
    label: 'Video URL (YouTube or Vimeo)',
    default: ''
  },
  caption: {
    type: 'text',
    label: 'Caption',
    default: ''
  },
  aspectRatio: {
    type: 'select',
    label: 'Aspect Ratio',
    options: [
      { label: '16:9', value: '16:9' },
      { label: '4:3', value: '4:3' },
      { label: '1:1', value: '1:1' }
    ],
    default: '16:9'
  },
  maxWidth: {
    type: 'text',
    label: 'Max Width',
    default: '800px'
  },
  align: {
    type: 'select',
    label: 'Alignment',
    options: [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' }
    ],
    default: 'center'
  },
  padding: {
    type: 'text',
    label: 'Padding',
    default: '1rem'
  }
};

export default VideoBlock; 